/**
 * Shared channel helpers (W3a, sub:derived): the prose word sequence, the prose sentence list,
 * section-boundary positions and small evidence utilities. Everything expensive is cached per
 * `Doc` in a WeakMap, so several channels can share one pass.
 *
 * Conventions
 * - "Prose" means the prose register only (ENGINE-SPEC §2.2): paragraphs whose own block is
 *   prose. Structural blocks attached to a paragraph are never included.
 * - A *sequence position* is an index into the prose word stream (or the prose sentence list).
 *   Sections are `doc.sections` (§2.8a: scenes, else chapters, else the whole document);
 *   `secStart[i]` is the sequence position at which the section containing position i begins,
 *   which is the right-censoring boundary of the recurrence channels (§3.3).
 */
import {
	type Doc,
	REGISTER_PROSE,
	SENT_RUNON,
	SENT_STRUCTURAL,
	TOK_WORD,
	type UnitRange,
} from '../document/types.ts';
import { loadEnglishLexicons } from '../lang/en/lexicons/index.ts';
import {
	type RecurrenceCurve,
	type SeedEquivalent,
	recurrenceAt,
	seedEquivalent,
} from '../segments/recurrence.ts';
import { makeRng } from '../util/rng.ts';
import { abstainResult, languageGate, makeEvidence, scoredResult } from './framework.ts';
import type { ChannelDef, ChannelResult, EvidenceBuilder, RunContext } from './types.ts';

/* ---------------- prose word sequence ---------------- */

export interface ProseWordSeq {
	/** Number of prose word tokens. */
	count: number;
	/** Doc token index of each prose word. */
	tok: Uint32Array;
	/** Folded intern id of each prose word (`doc.tokens.id`). */
	id: Uint32Array;
	/** Paragraph index of each prose word. */
	para: Uint32Array;
	/** Sequence position of the start of the section containing each word. */
	secStart: Int32Array;
	/** Length P+1: number of prose words in paragraphs < p (so paragraph p spans [paraFirst[p], paraFirst[p+1])). */
	paraFirst: Uint32Array;
}

const wordCache = new WeakMap<Doc, ProseWordSeq>();
const sentCache = new WeakMap<Doc, ProseSentenceList>();
const sectionCache = new WeakMap<Doc, Int32Array>();
const stopCache = new WeakMap<Doc, Uint8Array>();
const proseParaCache = new WeakMap<Doc, Uint32Array>();

/**
 * Paragraph index → first paragraph of its section (`doc.sections`; length P). Paragraphs outside
 * every section (cannot happen for a built Doc) map to themselves.
 */
export function sectionStartPara(doc: Doc): Int32Array {
	let out = sectionCache.get(doc);
	if (out) return out;
	const P = doc.paragraphs.count;
	out = new Int32Array(P);
	for (let p = 0; p < P; p++) out[p] = p;
	for (const s of doc.sections as UnitRange[]) {
		for (let p = s.paraStart; p < s.paraEnd && p < P; p++) out[p] = s.paraStart;
	}
	sectionCache.set(doc, out);
	return out;
}

/** Indices of the prose-register paragraphs whose own block has at least one word (ascending). */
export function proseParagraphs(doc: Doc): Uint32Array {
	let out = proseParaCache.get(doc);
	if (out) return out;
	const pp = doc.paragraphs;
	const list: number[] = [];
	for (let p = 0; p < pp.count; p++)
		if (pp.register[p] === REGISTER_PROSE && (pp.ownWords[p] as number) > 0) list.push(p);
	out = Uint32Array.from(list);
	proseParaCache.set(doc, out);
	return out;
}

/** The prose word stream with token→paragraph and section-boundary maps (cached per Doc). */
export function proseWordSeq(doc: Doc): ProseWordSeq {
	const hit = wordCache.get(doc);
	if (hit) return hit;
	const pp = doc.paragraphs;
	const P = pp.count;
	const T = doc.tokens;
	let n = 0;
	for (let p = 0; p < P; p++) if (pp.register[p] === REGISTER_PROSE) n += pp.ownWords[p] as number;
	const tok = new Uint32Array(n);
	const id = new Uint32Array(n);
	const para = new Uint32Array(n);
	const paraFirst = new Uint32Array(P + 1);
	let w = 0;
	for (let p = 0; p < P; p++) {
		paraFirst[p] = w;
		if (pp.register[p] !== REGISTER_PROSE) continue;
		const b = doc.blocks[pp.block[p] as number];
		if (!b) continue;
		for (let t = b.tokStart; t < b.tokEnd; t++) {
			if (T.cls[t] !== TOK_WORD) continue;
			tok[w] = t;
			id[w] = T.id[t] as number;
			para[w] = p;
			w++;
		}
	}
	paraFirst[P] = w;
	const secPara = sectionStartPara(doc);
	const secStart = new Int32Array(n);
	for (let i = 0; i < n; i++)
		secStart[i] = paraFirst[secPara[para[i] as number] as number] as number;
	const out: ProseWordSeq = { count: n, tok, id, para, secStart, paraFirst };
	wordCache.set(doc, out);
	return out;
}

/* ---------------- prose sentence list ---------------- */

export interface ProseSentenceList {
	/** Prose non-structural sentences, in document order. */
	count: number;
	/** Index into `doc.sentences`. */
	sent: Uint32Array;
	/** Paragraph of each sentence. */
	para: Uint32Array;
	/** Word tokens per sentence (may be 0 for number-only units). */
	words: Uint32Array;
	/** `doc.sentences.flags` (SENT_RUNON etc.). */
	flags: Uint8Array;
	/** Doc token index of the sentence's first WORD token, or −1 when it has none. */
	firstWord: Int32Array;
	/** List position of the first prose sentence of the section containing each sentence. */
	secStart: Int32Array;
	/** Length P+1: number of listed sentences in paragraphs < p. */
	paraFirst: Uint32Array;
}

/** Prose non-structural sentences with per-sentence metadata (cached per Doc). */
export function proseSentenceList(doc: Doc): ProseSentenceList {
	const hit = sentCache.get(doc);
	if (hit) return hit;
	const S = doc.sentences;
	const T = doc.tokens;
	const P = doc.paragraphs.count;
	const idx: number[] = [];
	for (let s = 0; s < S.count; s++) {
		if (S.register[s] !== REGISTER_PROSE) continue;
		if ((S.flags[s] as number) & SENT_STRUCTURAL) continue;
		idx.push(s);
	}
	const n = idx.length;
	const sent = Uint32Array.from(idx);
	const para = new Uint32Array(n);
	const words = new Uint32Array(n);
	const flags = new Uint8Array(n);
	const firstWord = new Int32Array(n).fill(-1);
	const paraFirst = new Uint32Array(P + 1);
	let cursor = 0;
	for (let i = 0; i < n; i++) {
		const s = sent[i] as number;
		const p = S.para[s] as number;
		para[i] = p;
		words[i] = S.words[s] as number;
		flags[i] = S.flags[s] as number;
		for (let t = S.tokStart[s] as number; t < (S.tokEnd[s] as number); t++) {
			if (T.cls[t] === TOK_WORD) {
				firstWord[i] = t;
				break;
			}
		}
		// paraFirst[q] for all q ≤ p not yet assigned
		while (cursor <= p) paraFirst[cursor++] = i;
	}
	while (cursor <= P) paraFirst[cursor++] = n;
	const secPara = sectionStartPara(doc);
	const secStart = new Int32Array(n);
	for (let i = 0; i < n; i++)
		secStart[i] = paraFirst[secPara[para[i] as number] as number] as number;
	const out: ProseSentenceList = {
		count: n,
		sent,
		para,
		words,
		flags,
		firstWord,
		secStart,
		paraFirst,
	};
	sentCache.set(doc, out);
	return out;
}

/* ---------------- FRAME_STOP ---------------- */

/**
 * 0/1 flag per intern id: the pack's FRAME_STOP words (PROC-01 "distinctive" test, PROC-08
 * anchoring). Ids of words the document never uses cannot occur, so they are simply absent.
 */
export function frameStopFlags(doc: Doc): Uint8Array {
	let out = stopCache.get(doc);
	if (out) return out;
	out = new Uint8Array(doc.tokens.vocabSize);
	const list = loadEnglishLexicons().processPack.lists.frameStop ?? [];
	for (const w of list) {
		const id = doc.tokens.lookup(w);
		if (id >= 0) out[id] = 1;
	}
	stopCache.set(doc, out);
	return out;
}

/* ---------------- recurrence helpers ---------------- */

/** Minimum positions at risk for an "estimable" lag: half the sample, clamped to [3, 30] (short texts stay usable). */
export const minAtRiskFor = (n: number): number => Math.max(3, Math.min(30, Math.floor(n / 2)));

/**
 * `seedEquivalent` with one extension: when the survival curve is ABSORBED (S = 0) at the largest
 * estimable lag, every position with that lookback has recurred, so R(k) = 1 for all larger k and
 * the statistic at the full reference size n0 is known exactly (otherwise a text in which every
 * sentence opens alike would report a tiny effective size and κ, the opposite of the truth).
 */
export function seedEquivalentAbsorbing(
	c: RecurrenceCurve,
	n0: number,
	minAtRisk: number
): SeedEquivalent {
	const est = seedEquivalent(c, n0, minAtRisk);
	if (!est.shortLookback) return est;
	const lstar = est.n1 - 1;
	if (lstar < 1 || (c.S[lstar] as number) > 0) return est;
	let sum = 0;
	for (let k = 0; k < n0; k++) sum += k >= lstar ? 1 : recurrenceAt(c, k);
	return { value: sum / n0, n1: n0, shortLookback: false, kappaFactor: 1 };
}

/**
 * Greenwood standard error of the seed-equivalent mean-R statistic: the largest sd of S(k) over
 * the first `n1` lags (conservative; the mean of the R(k) has at most this standard error).
 */
export function greenwoodSe(c: RecurrenceCurve, n1: number): number {
	let m = 0;
	const top = Math.min(n1, c.lmax + 1);
	for (let k = 0; k < top; k++) {
		const v = c.varS[k] as number;
		if (v > m) m = v;
	}
	return Math.sqrt(m);
}

/** sd of R at one lag. */
export const greenwoodSeAt = (c: RecurrenceCurve, lag: number): number =>
	Math.sqrt(c.varS[Math.min(Math.max(lag, 0), c.lmax)] as number);

/**
 * Adds merged runs of consecutive flagged sequence positions as evidence spans. Position i covers
 * tokens `tok[i] … tok[i + width − 1]` (width 1 for single-word items). `weightOf(i)` is the
 * weight recorded for each run (the largest weight in the run).
 */
export function addRunSpans(
	doc: Doc,
	ev: EvidenceBuilder,
	seq: ProseWordSeq,
	flagged: (i: number) => boolean,
	n: number,
	width: number,
	weightOf: (i: number) => number,
	feature = 0
): void {
	const T = doc.tokens;
	let i = 0;
	while (i < n) {
		if (!flagged(i)) {
			i++;
			continue;
		}
		let j = i;
		let wmax = weightOf(i);
		while (j + 1 < n && flagged(j + 1)) {
			j++;
			const w = weightOf(j);
			if (w > wmax) wmax = w;
		}
		const firstTok = seq.tok[i] as number;
		const lastTok = seq.tok[Math.min(j + width - 1, seq.count - 1)] as number;
		ev.add(
			T.start[firstTok] as number,
			(T.start[lastTok] as number) + (T.len[lastTok] as number),
			feature,
			wmax
		);
		i = j + 1;
	}
}

/** New evidence builder (re-export so channel files import one module). */
export { makeEvidence };

/* ---------------- small numeric helpers ---------------- */

export const SMALL = 1e-6;

/** Number formatting used by the seed-format summaries. */
export const fmt = (x: number, d = 1): string => (Math.round(x * 10 ** d) / 10 ** d).toFixed(d);

/** Deterministic per-channel RNG seed: document hash mixed with a channel salt (order independent). */
export const channelSeed = (doc: Doc, salt: number): number => (doc.hash ^ salt) >>> 0;

/* ---------------- joint segments (derived-phase channels) ---------------- */

export interface ParaRange {
	paraStart: number;
	paraEnd: number;
}

/**
 * The joint segmentation's paragraph ranges (§2.8b). Without `ctx.joint` (channel run stand-alone)
 * the whole document is one segment.
 */
export function jointRanges(ctx: RunContext): ParaRange[] {
	const j = ctx.joint;
	if (j && j.segments.length > 0) return j.segments;
	return [{ paraStart: 0, paraEnd: ctx.doc.paragraphs.count }];
}

/** paragraph → segment index for `ranges` (length P). */
export function segmentOfParagraph(ranges: ParaRange[], P: number): Int32Array {
	const out = new Int32Array(P);
	for (let s = 0; s < ranges.length; s++) {
		const r = ranges[s] as ParaRange;
		for (let p = r.paraStart; p < r.paraEnd && p < P; p++) out[p] = s;
	}
	return out;
}

/* ---------------- pooled within-segment dispersion (RH-01…03; §3.5) ---------------- */

/**
 * Resampling items (one per paragraph block, in document order, so segment indices are
 * non-decreasing) with the sufficient statistics of their sentence lengths or paragraph length:
 * n items' values, Σx, Σx², Σ|Δx| over the successive differences attributed to the item, and
 * the number of those differences.
 */
export interface DispItems {
	count: number;
	n: Float64Array;
	s1: Float64Array;
	s2: Float64Array;
	d: Float64Array;
	c: Float64Array;
	seg: Int32Array;
}

/** Per-segment accumulators. */
export interface DispAcc {
	n: Float64Array;
	s1: Float64Array;
	s2: Float64Array;
	d: Float64Array;
	c: Float64Array;
}

export const newAcc = (nSeg: number): DispAcc => ({
	n: new Float64Array(nSeg),
	s1: new Float64Array(nSeg),
	s2: new Float64Array(nSeg),
	d: new Float64Array(nSeg),
	c: new Float64Array(nSeg),
});

export function accumulate(items: DispItems, nSeg: number): DispAcc {
	const a = newAcc(nSeg);
	for (let i = 0; i < items.count; i++) {
		const s = items.seg[i] as number;
		a.n[s] = (a.n[s] as number) + (items.n[i] as number);
		a.s1[s] = (a.s1[s] as number) + (items.s1[i] as number);
		a.s2[s] = (a.s2[s] as number) + (items.s2[i] as number);
		a.d[s] = (a.d[s] as number) + (items.d[i] as number);
		a.c[s] = (a.c[s] as number) + (items.c[i] as number);
	}
	return a;
}

export interface PooledCv {
	/** sqrt(ΣSS / Σ(n−1)) / pooled mean · (1 + 1/(4N)); NaN when there is no within-segment replication. */
	value: number;
	/** Σ (n_s − 1). */
	df: number;
	/** Σ n_s. */
	N: number;
	mean: number;
}

/** RH-01 / RH-03 statistic (§3.5): pooled within-segment CV with the small-sample factor. */
export function pooledCv(a: DispAcc): PooledCv {
	let ss = 0;
	let df = 0;
	let N = 0;
	let S1 = 0;
	for (let s = 0; s < a.n.length; s++) {
		const n = a.n[s] as number;
		if (n <= 0) continue;
		const s1 = a.s1[s] as number;
		ss += Math.max(0, (a.s2[s] as number) - (s1 * s1) / n);
		df += n - 1;
		N += n;
		S1 += s1;
	}
	const mean = N > 0 ? S1 / N : 0;
	if (df <= 0 || mean <= 0) return { value: Number.NaN, df, N, mean };
	return { value: (Math.sqrt(ss / df) / mean) * (1 + 1 / (4 * N)), df, N, mean };
}

export interface PooledMasd {
	/** (ΣD / Σc) / pooled mean; NaN when there are no within-segment successive differences. */
	value: number;
	/** Σ c_s (number of successive differences). */
	diffs: number;
	N: number;
	mean: number;
}

/** RH-02 statistic (§3.5): pooled within-segment MASD over the pooled mean. */
export function pooledMasd(a: DispAcc): PooledMasd {
	let D = 0;
	let C = 0;
	let N = 0;
	let S1 = 0;
	for (let s = 0; s < a.n.length; s++) {
		D += a.d[s] as number;
		C += a.c[s] as number;
		N += a.n[s] as number;
		S1 += a.s1[s] as number;
	}
	const mean = N > 0 ? S1 / N : 0;
	if (C <= 0 || mean <= 0) return { value: Number.NaN, diffs: C, N, mean };
	return { value: D / C / mean, diffs: C, N, mean };
}

/**
 * Stratified block bootstrap over the items (paragraph blocks) within each segment. Deterministic
 * for a given `rng`. Returns the finite replicate statistics.
 */
export function bootstrapItems(
	items: DispItems,
	nSeg: number,
	rng: () => number,
	stat: (a: DispAcc) => number,
	maxDraws = 3_000_000
): number[] {
	if (items.count === 0) return [];
	const B = Math.max(40, Math.min(200, Math.floor(maxDraws / items.count)));
	const first = new Int32Array(nSeg + 1);
	{
		let i = 0;
		for (let s = 0; s <= nSeg; s++) {
			while (i < items.count && (items.seg[i] as number) < s) i++;
			first[s] = i;
		}
	}
	const acc = newAcc(nSeg);
	const out: number[] = [];
	for (let b = 0; b < B; b++) {
		acc.n.fill(0);
		acc.s1.fill(0);
		acc.s2.fill(0);
		acc.d.fill(0);
		acc.c.fill(0);
		for (let s = 0; s < nSeg; s++) {
			const a = first[s] as number;
			const m = (first[s + 1] as number) - a;
			for (let r = 0; r < m; r++) {
				const i = a + Math.floor(rng() * m);
				acc.n[s] = (acc.n[s] as number) + (items.n[i] as number);
				acc.s1[s] = (acc.s1[s] as number) + (items.s1[i] as number);
				acc.s2[s] = (acc.s2[s] as number) + (items.s2[i] as number);
				acc.d[s] = (acc.d[s] as number) + (items.d[i] as number);
				acc.c[s] = (acc.c[s] as number) + (items.c[i] as number);
			}
		}
		const v = stat(acc);
		if (Number.isFinite(v)) out.push(v);
	}
	return out;
}

/** 90% percentile interval and sd of replicate statistics; null when there are too few. */
export function bootSummary(
	vals: number[],
	estimate: number
): { ci: [number, number]; se: number } | null {
	if (vals.length < 20) return null;
	const v = [...vals].sort((a, b) => a - b);
	const lo = v[Math.floor(0.05 * v.length)] as number;
	const hi = v[Math.min(v.length - 1, Math.ceil(0.95 * v.length) - 1)] as number;
	let m = 0;
	for (const x of v) m += x;
	m /= v.length;
	let ss = 0;
	for (const x of v) ss += (x - m) ** 2;
	return {
		ci: [Math.min(lo, estimate), Math.max(hi, estimate)],
		se: Math.sqrt(ss / (v.length - 1)),
	};
}

/**
 * Analytic standard error of a CV under approximate normality, CV·√((1+2CV²)/(2n)), used when
 * there are too few paragraph blocks to bootstrap (< 8) and by PROC-02.
 */
export const cvSe = (cv: number, n: number): number =>
	cv * Math.sqrt((1 + 2 * cv * cv) / (2 * Math.max(n, 1)));

/** Word counts of the prose sentences used by RH-01/RH-02: words > 0 and not run-on flagged (§2.6 R12). */
export function rhythmSentenceLengths(doc: Doc): { len: Float64Array; para: Uint32Array } {
	const list = proseSentenceList(doc);
	const len: number[] = [];
	const para: number[] = [];
	for (let i = 0; i < list.count; i++) {
		const w = list.words[i] as number;
		if (w === 0 || (list.flags[i] as number) & SENT_RUNON) continue;
		len.push(w);
		para.push(list.para[i] as number);
	}
	return { len: Float64Array.from(len), para: Uint32Array.from(para) };
}

/**
 * Paragraph-block items over sentence lengths for the given segment map (a sentence's segment is
 * its paragraph's). Successive differences never span a segment boundary.
 */
export function sentenceItems(
	len: Float64Array,
	para: Uint32Array,
	segOfPara: Int32Array
): DispItems {
	const n: number[] = [];
	const s1: number[] = [];
	const s2: number[] = [];
	const d: number[] = [];
	const c: number[] = [];
	const seg: number[] = [];
	let cur = -1;
	let prevSeg = -1;
	let prevLen = 0;
	for (let i = 0; i < len.length; i++) {
		const p = para[i] as number;
		const x = len[i] as number;
		const sg = segOfPara[p] as number;
		if (p !== cur) {
			cur = p;
			n.push(0);
			s1.push(0);
			s2.push(0);
			d.push(0);
			c.push(0);
			seg.push(sg);
		}
		const j = n.length - 1;
		n[j] = (n[j] as number) + 1;
		s1[j] = (s1[j] as number) + x;
		s2[j] = (s2[j] as number) + x * x;
		if (prevSeg === sg) {
			d[j] = (d[j] as number) + Math.abs(x - prevLen);
			c[j] = (c[j] as number) + 1;
		}
		prevSeg = sg;
		prevLen = x;
	}
	return {
		count: n.length,
		n: Float64Array.from(n),
		s1: Float64Array.from(s1),
		s2: Float64Array.from(s2),
		d: Float64Array.from(d),
		c: Float64Array.from(c),
		seg: Int32Array.from(seg),
	};
}

/** Paragraph items for RH-03: one item per prose paragraph (its own-block word count). */
export function paragraphItems(doc: Doc, segOfPara: Int32Array): DispItems {
	const ps = proseParagraphs(doc);
	const m = ps.length;
	const out: DispItems = {
		count: m,
		n: new Float64Array(m).fill(1),
		s1: new Float64Array(m),
		s2: new Float64Array(m),
		d: new Float64Array(m),
		c: new Float64Array(m),
		seg: new Int32Array(m),
	};
	for (let i = 0; i < m; i++) {
		const p = ps[i] as number;
		const w = doc.paragraphs.ownWords[p] as number;
		out.s1[i] = w;
		out.s2[i] = w * w;
		out.seg[i] = segOfPara[p] as number;
	}
	return out;
}

/** Relabel every item into a single segment (fallback when the joint segmentation is too fine). */
export function singleSegment(items: DispItems): DispItems {
	return { ...items, seg: new Int32Array(items.count) };
}

/** Between-segment dispersion of the segment means: size-weighted CV of the means (rhythm drift diagnostic). */
export function segmentMeanCv(a: DispAcc): number {
	let N = 0;
	let S1 = 0;
	for (let s = 0; s < a.n.length; s++) {
		N += a.n[s] as number;
		S1 += a.s1[s] as number;
	}
	if (N <= 0 || S1 <= 0) return 0;
	const mean = S1 / N;
	let v = 0;
	for (let s = 0; s < a.n.length; s++) {
		const n = a.n[s] as number;
		if (n <= 0) continue;
		v += n * ((a.s1[s] as number) / n - mean) ** 2;
	}
	return Math.sqrt(v / N) / mean;
}

/* ---------------- shared runner for the pooled within-segment channels ---------------- */

export interface DispersionSpec {
	def: ChannelDef;
	/** Mixed into the document hash for the deterministic bootstrap RNG. */
	salt: number;
	/** Build the resampling items for a paragraph→segment map; `exposure` is the count checked against `minExposure`. */
	build(segOfPara: Int32Array): { items: DispItems; exposure: number };
	/** The pooled statistic; `df` is the effective sample (Σ(n−1) or the difference count). */
	stat(acc: DispAcc): { value: number; df: number; N: number; mean: number };
	/** Analytic-SE multiplier used when there are too few blocks to bootstrap. */
	analyticFactor: number;
	/** Extra features and summary lines from the final accumulators. */
	extra(acc: DispAcc, items: DispItems): { features: Record<string, number>; summary: string[] };
}

/**
 * Pooled within-joint-segment dispersion channel (RH-01/02/03, ENGINE-SPEC §3.5). The statistic is
 * pooled over the joint segments (or the whole document when run stand-alone or when the
 * segmentation is so fine that no within-segment replication is left), the interval is a
 * stratified paragraph-block bootstrap (deterministic; analytic fallback for < 8 blocks), and
 * the between-segment dispersion is reported as a separate feature. K = E = the exposure count.
 */
export function runDispersion(ctx: RunContext, spec: DispersionSpec): ChannelResult {
	const def = spec.def;
	const gate = languageGate(def, ctx);
	if (gate.abstain) return abstainResult(def, gate.abstain);
	const notes: string[] = [];
	if (gate.note) notes.push(gate.note);
	const ranges = jointRanges(ctx);
	const P = ctx.doc.paragraphs.count;
	const segOf = segmentOfParagraph(ranges, P);
	let nSeg = ranges.length;
	const built = spec.build(segOf);
	if (built.exposure < def.minExposure)
		return abstainResult(
			def,
			`insufficient text: ${built.exposure} ${def.exposureUnit} < ${def.minExposure}`,
			notes
		);
	let items = built.items;
	let acc = accumulate(items, nSeg);
	let pooled = spec.stat(acc);
	const minDf = def.minExposure - 1;
	if (!(pooled.df >= minDf)) {
		items = singleSegment(items);
		nSeg = 1;
		acc = accumulate(items, 1);
		pooled = spec.stat(acc);
		if (ranges.length > 1)
			notes.push(
				'joint segmentation too fine to pool within segments; pooled over the whole document'
			);
	}
	if (!Number.isFinite(pooled.value))
		return abstainResult(def, 'insufficient within-segment replication', notes);

	const value = pooled.value;
	const rng = makeRng(channelSeed(ctx.doc, spec.salt));
	const boot =
		items.count >= 8
			? bootSummary(
					bootstrapItems(items, nSeg, rng, (a) => spec.stat(a).value),
					value
				)
			: null;
	let ci90: [number, number];
	let se: number;
	if (boot) {
		ci90 = boot.ci;
		se = boot.se;
	} else {
		se = cvSe(value, pooled.df) * spec.analyticFactor;
		ci90 = [Math.max(0, value - 1.645 * se), value + 1.645 * se];
		notes.push('analytic interval (fewer than 8 paragraph blocks)');
	}
	const kappa = pooled.df / (pooled.df + def.prior.m);
	let occupied = 0;
	for (let s = 0; s < nSeg; s++) if ((acc.n[s] as number) > 0) occupied++;
	const extra = spec.extra(acc, items);
	return scoredResult(def, {
		K: built.exposure,
		E: built.exposure,
		raw: value,
		estimate: value,
		ci90,
		deviation: (def.direction * (value - def.prior.mu)) / Math.max(se, SMALL),
		phi: 1,
		kappa,
		notes,
		features: {
			value,
			exposure: built.exposure,
			df: pooled.df,
			mean: pooled.mean,
			segments: occupied,
			betweenSegmentCv: segmentMeanCv(acc),
			...extra.features,
		},
		summary: extra.summary,
	});
}
